// passkey.js — the WebAuthn client. IDENTIFY binds a passkey onto the caller's OWN anon GUID: the
// bind target is NEVER asserted by the client, the server derives it from the anon EBA token we
// present, so registering DECORATES the same GUID and every existing room stays attached. LOG IN
// presents the passkey; the server looks the credential up by id (the subject comes from the
// STORE, never the caller), mints a HUMAN cap-token, and we adopt it as the WT Bearer. Endpoints:
// POST /webauthn/{register,login}/{begin,finish}; every binary field is base64url.

import { b64url, b64urlDecode } from "./eba.js";
import { prfSalt } from "./envelope.js";

function originBase() {
  return (typeof location !== "undefined" && location.origin) || "";
}

async function postJSON(path, body) {
  const res = await fetch(originBase() + path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
    credentials: "include",   // the login/finish Set-Cookie (session capability) must stick
  });
  const text = await res.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch (_e) { /* non-JSON */ }
  if (!res.ok) {
    const msg = (json && json.error) || text || ("HTTP " + res.status);
    throw new Error("[passkey] " + path + " -> " + res.status + ": " + msg);
  }
  return json;
}

function anonToken() {
  return (typeof window !== "undefined" && window.__coglativeAnonToken) || "";
}

export async function registerPasskey() {
  const eba = anonToken();
  if (!eba) throw new Error("[passkey] no anon session — identify needs an active device key");

  const begin = await postJSON("/webauthn/register/begin", {});
  const publicKeyOptions = begin.publicKey;

  // The server omits `user` (a server-side bind) but WebAuthn REQUIRES a handle, so pass the anon
  // GUID. residentKey:'required' makes it DISCOVERABLE, which is what lets login find it later.
  const guid = publicKeyOptions.subjectHint || "anon";
  const enc = new TextEncoder();
  const publicKey = {
    challenge: b64urlDecode(publicKeyOptions.challenge),
    rp: publicKeyOptions.rp,
    user: {
      id: enc.encode(guid),
      name: guid,
      displayName: "Coglative account",
    },
    pubKeyCredParams: publicKeyOptions.pubKeyCredParams,
    authenticatorSelection: Object.assign(
      { residentKey: "required", requireResidentKey: true },
      publicKeyOptions.authenticatorSelection || {}
    ),
    timeout: 60000,
    attestation: "none",
  };

  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error("[passkey] create() returned no credential");

  const resp = cred.response; // AuthenticatorAttestationResponse
  const out = await postJSON("/webauthn/register/finish", {
    handle: begin.handle,
    eba_token: eba,
    client_data_json: b64url(new Uint8Array(resp.clientDataJSON)),
    attestation_object: b64url(new Uint8Array(resp.attestationObject)),
  });
  if (!out || out.ok !== true) throw new Error("[passkey] register/finish not ok");
  // Identify-in-place mints NO token, so the session stays self-signed ANON and each device keys on
  // its OWN GUID — two devices sharing the passkey never converge. Logging in mints the HUMAN token
  // whose subject is the ACCOUNT GUID, so every device holding it resolves to ONE account
  // (convergence, never merge — SPEC-identity-model-v0).
  await loginPasskey();
  return out.subject;
}

// ─── log in (present a passkey, adopt the minted HUMAN token) ────────────────
// Discoverable: no allowCredentials, so the platform offers whatever passkeys it holds for this RP.
// PRF (#35): { tenantId } also requests the `prf` extension with the per-tenant salt. Its result is
// INPUT KEYING MATERIAL for the envelope KEK, never the key, and NO result means the caller MUST
// treat encryption as unavailable, never as licence to store plaintext.
export async function loginPasskey(opts = {}) {
  const { tenantId } = opts;
  const begin = await postJSON("/webauthn/login/begin", {});
  const pk = begin.publicKey;

  const publicKey = {
    challenge: b64urlDecode(pk.challenge),
    rpId: pk.rpId,
    userVerification: pk.userVerification || "preferred",
    timeout: 60000,
  };
  if (tenantId) {
    publicKey.extensions = { prf: { eval: { first: prfSalt(tenantId) } } };
  }

  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error("[passkey] get() returned no credential");

  const resp = cred.response;
  // The server's lookup key AND the wrapped-DEK row key in e2ee-session.js (#35).
  const credentialId = b64url(new Uint8Array(cred.rawId));
  const out = await postJSON("/webauthn/login/finish", {
    handle: begin.handle,
    credential_id: credentialId,
    authenticator_data: b64url(new Uint8Array(resp.authenticatorData)),
    client_data_json: b64url(new Uint8Array(resp.clientDataJSON)),
    signature: b64url(new Uint8Array(resp.signature)),
  });
  if (!out || !out.access_token) throw new Error("[passkey] login/finish returned no token");

  // OAuthClient is the single cap-token source of truth: it persists durably (without that hop the
  // token lived only in memory and every reload re-prompted) and publishes the WT Bearer.
  //
  // The 7-day refresh grant rides the SAME lane and is adopted FIRST: the access token dies in 15
  // minutes, and adoptCapToken can kick a live-channel rebind whose recovery wants a grant already
  // in hand. Persisting only the access token expired a logged-in human back to the anon gate a
  // quarter-hour after login. The grant is a SECRET: never returned from here, logged, or in a URL.
  if (typeof window !== "undefined"
      && window.OAuthClient && typeof window.OAuthClient.adoptCapToken === "function") {
    if (out.refresh_token && typeof window.OAuthClient.adoptRefreshToken === "function") {
      window.OAuthClient.adoptRefreshToken(out.refresh_token);
    }
    window.OAuthClient.adoptCapToken(out.access_token);
  } else if (typeof window !== "undefined") {
    window.__coglativeAnonToken = out.access_token;
  }

  let prfAvailable = false;
  let prfOutput = null;
  if (tenantId) {
    const ext = (typeof cred.getClientExtensionResults === "function")
      ? (cred.getClientExtensionResults() || {}) : {};
    const first = ext.prf && ext.prf.results && ext.prf.results.first;
    if (first) {
      prfAvailable = true;
      prfOutput = new Uint8Array(first); // the 32-byte IKM for envelope.deriveKek
    }
  }

  return { accessToken: out.access_token, subject: out.subject, credentialId, prfAvailable, prfOutput };
}

export function passkeysAvailable() {
  return typeof window !== "undefined"
    && !!(window.PublicKeyCredential && navigator.credentials && navigator.credentials.create);
}
